import {useQuery} from '@tanstack/react-query';

import * as ZonesService from '../services/ZonesService';
import {zonesKeys} from './useZonesQuery';
import {ZoneCreateConnection} from '../types';

// FNP-1191 CRUD half — WHICH CONNECTIONS MAY BE OFFERED IN THE CREATE FORM.
//
// ⚠️ THE SAME "NOT PERMITTED BY DEFAULT" RULE AS useZoneWriteCapabilities,
// APPLIED ONE LEVEL DOWN.
//
// The Add button is gated on a connection that CAN create zones. The form then
// has to let the operator pick WHICH one — and if that picker offered every
// connection, a tenant would choose a read-only one and get PERMISSION_DENIED
// at submit. That is precisely the failure the hiding rule exists to prevent,
// reintroduced inside the form.
//
// So the same two tests apply per connection, and both must PASS to be offered:
//   status === 'ACTIVE'          — the credential works NOW
//   capabilities.canCreateZones  — and it proved it can do this
//
// Anything else — missing capabilities, a non-ACTIVE status, a null snapshot —
// is excluded. Absence is the correct answer for a connection that cannot.

// Exported for its own test: the filter is the whole point of the hook, and
// testing it through React Query would test the plumbing instead of the rule.
export const canCreateZonesThrough = (connection: ZoneCreateConnection): boolean => {
    // A connection that is not ACTIVE reports no write capability whatever its
    // stored snapshot says — the snapshot records what the credential proved AT
    // CONNECT TIME, and a non-ACTIVE status is the provider saying it does not
    // work now.
    if (connection.status !== 'ACTIVE') {
        return false;
    }
    // Explicitly `=== true`, not truthy: an absent snapshot must not pass.
    return connection.capabilities?.canCreateZones === true;
};

export const useZoneCreateConnections = () => {

    const {
        data,
        isLoading,
        isError,
    } = useQuery({
        queryKey: [...zonesKeys.all, 'create-connections'] as const,
        queryFn: async (): Promise<ZoneCreateConnection[]> =>
            await ZonesService.fetchConnections(),
        // Same reasoning as the capability snapshot: a connection's
        // capabilities change when someone re-connects a provider, not while a
        // form is open.
        staleTime: 300_000,
    });

    const connections = data ?? [];
    const writable = connections.filter(canCreateZonesThrough);

    return {
        connections: writable,
        loading: isLoading,
        isError,
        // ⚠️ DISTINCT FROM `writable.length === 0`, and the form needs both.
        //
        // "You have no connections" and "none of your connections can create
        // zones" are different problems with different fixes — connect a
        // provider, versus reconnect one with a credential that can write. One
        // message for both sends half the tenants to the wrong place.
        hasAnyConnection: connections.length > 0,
    };
};
